 <template>
  <v-dialog
    v-model="dialog"
    max-width="700px"
    persistent
    scrollable
  >
    <template #activator="{ on }">
      <v-btn
        small
        v-on="on"
        class="text-none"
        color="primary"
      >
        <v-icon small left>mdi-playlist-check</v-icon>
        {{ $t('downtime.assignReason') }}
      </v-btn>
    </template>
    <v-form ref="form" lazy-validation>
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('downtime.assignReasonTitle') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="selectedDowntimes"
            item-key="_id"
            disable-pagination
            hide-default-footer
          >
            <template #item="{ item }">
              <tr>
                <td>{{ item[fieldName] || item.machinename }}</td>
                <td>{{ item.shiftName }}</td>
                <td>
                  {{ new Date(item.downtimestart).toLocaleTimeString() }}
                </td>
                <td v-if="item.status !== 'inProgress'">
                  {{ new Date(item.downtimeend).toLocaleTimeString() }}
                </td>
                <td v-else>
                  {{ $t('downtime.na') }}
                </td>
                <td v-if="item.status !== 'inProgress'">
                  {{ duration(item.downtimeduration) }}
                </td>
                <td v-else>
                  {{ $t('downtime.ongoing') }}
                </td>
              </tr>
            </template>
          </v-data-table>
          <v-select
            dense
            outlined
            clearable
            :label="reasonGroupLabel"
            :items="reasonGroups"
            v-model="selectedReasonGroup"
            class="mt-4"
          />
          <v-autocomplete
            dense
            filled
            clearable
            hide-details
            return-object
            :label="$t('downtime.reason')"
            class="mt-4"
            :items="filteredReasons"
            item-text="reasonname"
            item-value="reasoncode"
            v-model="selectedReason"
            @change="checkSelection"
          >
            <template #selection="data">
              {{ data.item.reasoncode }} | {{ data.item.reasonname }}
            </template>
            <template #item="data">
              <v-list-item-content>
                <v-list-item-title>
                  {{ data.item.reasoncode }} |
                  {{ data.item.reasonname }} |
                  {{ data.item.category }} |
                  {{ data.item.department }}
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-text-field
            dense
            filled
            hide-details
            label="Remark"
            class="mt-4"
            v-model="remarkText"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="text-none"
            @click="saveReasons"
            :disabled="btnDisable"
            :loading="saving"
          >
            {{ $t('downtime.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} from 'vuex';
import { getPlannedValue } from '../utils/downtime-reason';
import { getDowntimeReasonGroupField } from '../utils/downtime-schema';

export default {
  name: 'AssignReasonDialog',
  data() {
    return {
      dialog: false,
      saving: false,
      selectedReason: null,
      selectedReasonGroup: null,
      remarkText: '',
      btnDisable: false,
      toggleHide: false,
      downtimes: [],
    };
  },
  computed: {
    ...mapState('webApp', ['appSchema']),
    ...mapState('downtimeLog', [
      'downtimeReasons',
      'selectedDowntimes',
    ]),
    ...mapGetters('downtimeLog', ['machineFilterField']),
    fieldName() {
      return this.machineFilterField;
    },
    fieldLabel() {
      return this.fieldName === 'linename'
        ? 'Line'
        : this.$t('downtime.machine');
    },
    reasonGroupField() {
      return getDowntimeReasonGroupField(this.appSchema);
    },
    reasonGroupLabel() {
      return this.$t(`downtime.${this.reasonGroupField}`);
    },
    headers() {
      return [
        {
          text: this.fieldLabel,
          value: this.fieldName,
        },
        {
          text: this.$t('downtime.shift'),
          value: 'shiftName',
        },
        {
          text: this.$t('downtime.from'),
          value: 'downtimestart',
        },
        {
          text: this.$t('downtime.to'),
          value: 'downtimeend',
        },
        {
          text: this.$t('downtime.duration'),
          value: 'downtimeduration',
        },
      ];
    },

    // Unique grouping values
    reasonGroups() {
      const groups = new Map();

      this.downtimeReasons.forEach((reason) => {
        const value = String(reason[this.reasonGroupField] || '').trim();

        if (value) {
          const normalizedValue = value.toLowerCase();
          if (!groups.has(normalizedValue)) {
            groups.set(normalizedValue, value);
          }
        }
      });

      return [...groups.values()];
    },

    // Filtered reasons based on the configured grouping field
    filteredReasons() {
      if (!this.selectedReasonGroup) return this.downtimeReasons;
      return this.downtimeReasons.filter(
        (r) => String(r[this.reasonGroupField] || '').trim().toLowerCase()
          === String(this.selectedReasonGroup || '').trim().toLowerCase(),
      );
    },
  },
  created() {
    this.checkSelection();
  },
  methods: {
    ...mapMutations('downtimeLog', ['setSelectedDowntimes', 'setToggleSelection']),
    ...mapActions('downtimeLog', ['updateReason']),
    duration(value) {
      const d = value;
      const h = Math.floor(d / 3600);
      const m = Math.floor((d % 3600) / 60);
      const s = Math.floor((d % 3600) % 60);
      return `${h.toString().padStart(2, 0)}:${m
        .toString()
        .padStart(2, 0)}:${s.toString().padStart(2, 0)}`;
    },
    checkSelection() {
      if (!this.selectedReason) {
        this.btnDisable = true;
      } else {
        this.btnDisable = false;
      }
    },
    async saveReasons() {
      this.saving = true;
      const payload = {
        reasonname: this.selectedReason.reasonname,
        reasoncode: this.selectedReason.reasoncode,
        category: this.selectedReason.category,
        department: this.selectedReason.department,

      };

      const isPlanned = getPlannedValue(this.selectedReason, this.downtimeReasons);

      if (isPlanned !== null) {
        payload.isPlanned = isPlanned;
      }

      const remark = this.remarkText.trim();

      if (remark) {
        payload.remark = remark;
      }

      await Promise.all(this.selectedDowntimes.map((item) => this.updateReason({
        // eslint-disable-next-line
        id: item._id,
        payload,
      })));
      this.setSelectedDowntimes([]);
      this.setToggleSelection(this.toggleHide);
      this.remarkText = '';
      this.saving = false;
      this.dialog = false;
    },
  },
  watch: {
    selectedReasonGroup() {
      this.selectedReason = null;
      this.btnDisable = true;
    },
  },
};
</script>
